import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Plus, Edit2, Trash2, Loader2, Save, X, Check,
  Languages, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { TranslateButton } from '../components/TranslateButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// دالة التحقق من أن النص عربي فقط
const isArabicOnly = (text) => {
  const arabicRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\s\d\-_.،؟!()]+$/;
  return arabicRegex.test(text) || text === '';
};

// دالة التحقق من أن النص إنجليزي فقط
const isEnglishOnly = (text) => {
  const englishRegex = /^[a-zA-Z\s\d\-_.,'?!&()]+$/;
  return englishRegex.test(text) || text === '';
};

export default function LegalTranslationServices() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { token } = useAuth();
  
  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // State
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    topic_ar: '',
    topic_en: '',
    requires_passport: false,
    requires_id: false,
    requires_photo: false,
    notes_ar: '',
    notes_en: ''
  });

  // Translation states removed — now handled by the shared <TranslateButton />
  // which also respects the Cloud GM AI Control Hub "auto_translation" feature flag.

  const getHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Fetch services
  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/legal-translation/services`, { headers: getHeaders() });
      setServices(res.data.data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الخدمات' : 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  // Add service
  const addService = async () => {
    if (!formData.topic_ar.trim() || !formData.topic_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الموضوع بالعربي والإنجليزي' : 'Please enter topic in both Arabic and English');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/legal-translation/services`, formData, { headers: getHeaders() });
      toast.success(language === 'ar' ? 'تم إضافة الخدمة بنجاح' : 'Service added successfully');
      setShowAddModal(false);
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الإضافة' : 'Failed to add'));
    } finally {
      setSaving(false);
    }
  };

  // Update service
  const updateService = async () => {
    if (!formData.topic_ar.trim() || !formData.topic_en.trim()) {
      toast.error(language === 'ar' ? 'يرجى إدخال الموضوع بالعربي والإنجليزي' : 'Please enter topic in both Arabic and English');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/legal-translation/services/${editingService.id}`,
        formData,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم تحديث الخدمة بنجاح' : 'Service updated successfully');
      setShowEditModal(false);
      setEditingService(null);
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في التحديث' : 'Failed to update'));
    } finally {
      setSaving(false);
    }
  };

  // Delete service
  const deleteService = async () => {
    setSaving(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/legal-translation/services/${deletingService.id}`,
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حذف الخدمة بنجاح' : 'Service deleted successfully');
      setShowDeleteModal(false);
      setDeletingService(null);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحذف' : 'Failed to delete'));
    } finally {
      setSaving(false);
    }
  };

  // Update checkbox in table
  const updateServiceCheckbox = (serviceId, field, value) => {
    setServices(prev => prev.map(s => 
      s.id === serviceId ? { ...s, [field]: value } : s
    ));
    setHasChanges(true);
  };

  // Update notes in table
  const updateServiceNotes = (serviceId, field, value) => {
    setServices(prev => prev.map(s => 
      s.id === serviceId ? { ...s, [field]: value } : s
    ));
    setHasChanges(true);
  };

  // Save all changes
  const saveAllChanges = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/legal-translation/services/bulk-update`,
        { services },
        { headers: getHeaders() }
      );
      toast.success(language === 'ar' ? 'تم حفظ التغييرات بنجاح' : 'Changes saved successfully');
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل في الحفظ' : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      topic_ar: '',
      topic_en: '',
      requires_passport: false,
      requires_id: false,
      requires_photo: false,
      notes_ar: '',
      notes_en: ''
    });
  };

  useEffect(() => {
    fetchServices();
  }, []);

  return (
    <DashboardLayout
      title="خدمات الترجمة القانونية"
      titleEn="Legal Translation Services"
      subtitle="إدارة مواضيع الترجمة القانونية"
      subtitleEn="Manage legal translation topics"
      backPath="/control/cloudgm"
      breadcrumb={[
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
        { labelAr: 'منصة العملاء', labelEn: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
        { labelAr: 'التحكم والمعلومات (منصة العملاء)', labelEn: 'Control & Info (Customers)', path: '/control/cloudgm' },
        { labelAr: 'خدمات الترجمة القانونية', labelEn: 'Legal Translation' }
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
              <Languages className="h-6 w-6 text-rose-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {language === 'ar' ? 'مواضيع الترجمة' : 'Translation Topics'}
              </h2>
              <p className="text-sm text-[#71717A]">
                {services.length} {language === 'ar' ? 'موضوع' : 'topics'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Button
                onClick={saveAllChanges}
                disabled={saving}
                className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
                {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
              </Button>
            )}
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="bg-[#d4af37] hover:bg-[#c9a227] text-black rounded-xl"
              data-testid="add-service-btn"
            >
              <Plus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'إضافة موضوع' : 'Add Topic'}
            </Button>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-rose-500 animate-spin" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <FileText className="h-16 w-16 text-[#808080] mx-auto mb-4" />
            <p className="text-[#808080] text-lg">
              {language === 'ar' ? 'لا توجد خدمات بعد' : 'No services yet'}
            </p>
            <Button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="mt-4 bg-rose-500 hover:bg-rose-600 text-white rounded-xl"
            >
              <Plus className="h-4 w-4 me-2" />
              {language === 'ar' ? 'أضف أول خدمة' : 'Add first service'}
            </Button>
          </div>
        ) : (
          <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2a2a4e] bg-[#0f0f1a]">
                    <th className="text-right text-white font-bold py-4 px-6 min-w-[200px]">
                      {language === 'ar' ? 'الموضوع' : 'Topic'}
                    </th>
                    <th className="text-center text-white font-bold py-4 px-4 w-24">
                      <div className="flex flex-col items-center">
                        <span>{language === 'ar' ? 'الجواز' : 'Passport'}</span>
                      </div>
                    </th>
                    <th className="text-center text-white font-bold py-4 px-4 w-24">
                      <div className="flex flex-col items-center">
                        <span>{language === 'ar' ? 'الهوية' : 'ID'}</span>
                      </div>
                    </th>
                    <th className="text-center text-white font-bold py-4 px-4 w-28">
                      <div className="flex flex-col items-center">
                        <span>{language === 'ar' ? 'صورة شخصية' : 'Photo'}</span>
                      </div>
                    </th>
                    <th className="text-right text-white font-bold py-4 px-6 min-w-[200px]">
                      {language === 'ar' ? 'الملاحظات' : 'Notes'}
                    </th>
                    <th className="text-center text-white font-bold py-4 px-4 w-24">
                      {language === 'ar' ? 'إجراءات' : 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service, index) => (
                    <tr 
                      key={service.id} 
                      className={`border-b border-[#2a2a4e] hover:bg-[#2a2a4e]/30 ${index % 2 === 0 ? 'bg-[#1a1a2e]' : 'bg-[#151525]'}`}
                    >
                      <td className="py-4 px-6">
                        <div>
                          <span className="text-white font-medium block">{service.topic_ar}</span>
                          <span className="text-[#808080] text-sm">{service.topic_en}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={service.requires_passport}
                            onCheckedChange={(checked) => updateServiceCheckbox(service.id, 'requires_passport', checked)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          />
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={service.requires_id}
                            onCheckedChange={(checked) => updateServiceCheckbox(service.id, 'requires_id', checked)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          />
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={service.requires_photo}
                            onCheckedChange={(checked) => updateServiceCheckbox(service.id, 'requires_photo', checked)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          />
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <Input
                          value={service.notes_ar || ''}
                          onChange={(e) => updateServiceNotes(service.id, 'notes_ar', e.target.value)}
                          placeholder={language === 'ar' ? 'ملاحظات...' : 'Notes...'}
                          className="bg-[#0a0a0a] border-[#2a2a4e] text-white text-sm"
                          dir="rtl"
                        />
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            onClick={() => {
                              setEditingService(service);
                              setFormData({
                                topic_ar: service.topic_ar,
                                topic_en: service.topic_en,
                                requires_passport: service.requires_passport,
                                requires_id: service.requires_id,
                                requires_photo: service.requires_photo,
                                notes_ar: service.notes_ar || '',
                                notes_en: service.notes_en || ''
                              });
                              setShowEditModal(true);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 p-2"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => {
                              setDeletingService(service);
                              setShowDeleteModal(true);
                            }}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 p-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        <Dialog open={showAddModal || showEditModal} onOpenChange={() => { setShowAddModal(false); setShowEditModal(false); setEditingService(null); resetForm(); }}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {showAddModal 
                  ? (language === 'ar' ? 'إضافة موضوع جديد' : 'Add New Topic')
                  : (language === 'ar' ? 'تعديل الموضوع' : 'Edit Topic')
                }
              </DialogTitle>
              <DialogDescription className="text-[#808080]">
                {language === 'ar' ? 'أدخل بيانات خدمة الترجمة القانونية' : 'Enter legal translation service details'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الموضوع بالعربي *' : 'Topic in Arabic *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.topic_ar}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isArabicOnly(value)) {
                        setFormData(prev => ({ ...prev, topic_ar: value }));
                      }
                    }}
                    placeholder={language === 'ar' ? 'مثال: ترجمة شهادة ميلاد' : 'e.g., Birth Certificate Translation'}
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                    dir="rtl"
                  />
                  {/* Shared TranslateButton — respects Cloud GM AI Control Hub flag */}
                  <TranslateButton
                    sourceText={formData.topic_en}
                    direction="en_to_ar"
                    onTranslated={(out) => setFormData(prev => ({ ...prev, topic_ar: out }))}
                    disabled={!formData.topic_en.trim()}
                    testId="legal-trans-translate-to-ar"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'الموضوع بالإنجليزي *' : 'Topic in English *'}
                </label>
                <div className="flex gap-2">
                  <Input
                    value={formData.topic_en}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (isEnglishOnly(value)) {
                        setFormData(prev => ({ ...prev, topic_en: value }));
                      }
                    }}
                    placeholder="e.g., Birth Certificate Translation"
                    className="bg-[#0a0a0a] border-[#2a2a4e] text-white flex-1"
                    dir="ltr"
                  />
                  <TranslateButton
                    sourceText={formData.topic_ar}
                    direction="ar_to_en"
                    onTranslated={(out) => setFormData(prev => ({ ...prev, topic_en: out }))}
                    disabled={!formData.topic_ar.trim()}
                    testId="legal-trans-translate-to-en"
                  />
                </div>
              </div>
              
              <div className="border-t border-[#2a2a4e] pt-4">
                <label className="block text-sm text-[#808080] mb-3">
                  {language === 'ar' ? 'المستندات المطلوبة' : 'Required Documents'}
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.requires_passport}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_passport: checked }))}
                      className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    <span className="text-white">{language === 'ar' ? 'الجواز' : 'Passport'}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.requires_id}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_id: checked }))}
                      className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    <span className="text-white">{language === 'ar' ? 'الهوية' : 'ID Card'}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={formData.requires_photo}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_photo: checked }))}
                      className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                    />
                    <span className="text-white">{language === 'ar' ? 'صورة شخصية' : 'Personal Photo'}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#808080] mb-2">
                  {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
                </label>
                <Input
                  value={formData.notes_ar}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes_ar: e.target.value }))}
                  placeholder={language === 'ar' ? 'ملاحظات إضافية...' : 'Additional notes...'}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  dir="rtl"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                onClick={() => { setShowAddModal(false); setShowEditModal(false); setEditingService(null); resetForm(); }}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={showAddModal ? addService : updateService}
                disabled={saving}
                className="bg-rose-500 hover:bg-rose-600 text-white"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  language === 'ar' ? 'حفظ' : 'Save'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={showDeleteModal} onOpenChange={() => { setShowDeleteModal(false); setDeletingService(null); }}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-400">
                {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
              </DialogTitle>
              <DialogDescription className="text-[#808080]">
                {language === 'ar' 
                  ? `هل أنت متأكد من حذف "${deletingService?.topic_ar}"؟`
                  : `Are you sure you want to delete "${deletingService?.topic_en}"?`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 mt-4">
              <Button
                onClick={() => { setShowDeleteModal(false); setDeletingService(null); }}
                variant="outline"
                className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={deleteService}
                disabled={saving}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  language === 'ar' ? 'حذف' : 'Delete'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </section>
    </DashboardLayout>
  );
}
